import React, { forwardRef, useEffect, useState, useImperativeHandle, useRef } from 'react';
import { Form, Input, DatePicker, Button, Checkbox, Divider, Select } from 'antd';
import { ProviderEducation } from 'types/provider';
import ExperiencePlaceholder from 'assets/common/experiencePlaceholder.svg';
import dayjs from 'dayjs';
import s from './s.module.less';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { IBackgroundInfo } from 'types/newUniprofile';
import { isEqual } from 'lodash';

interface Props {
    initValue?: ProviderEducation[];
    isEditing?: boolean;
    updateFn: (data: Partial<IBackgroundInfo>) => Promise<void>;
}

const Education = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, updateFn } = props;
    const [form] = Form.useForm();
    const [degreeOptions] = useGetSettingData(EnumFields.DEGREE);
    const [currentValue, setCurrentValue] = useState<ProviderEducation[] | null>(null);
    const [update, forceUpdate] = useState({});
    const prevInitValueRef = useRef<ProviderEducation[]>();

    useEffect(() => {
        if (initValue?.length && !isEqual(prevInitValueRef.current, initValue)) {
            form.setFieldsValue({
                providerEducationList: initValue.map((item) => ({
                    ...item,
                    startDate: item.startDate ? dayjs(item.startDate) : undefined,
                    endDate: item.endDate ? dayjs(item.endDate) : undefined,
                })),
            });
            setCurrentValue(initValue);
            prevInitValueRef.current = initValue;
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            await updateFn({
                providerEducationList: values.providerEducationList,
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));

    const renderAdd = (add: () => void) => {
        return (
            <div
                onClick={() => {
                    add();
                    const wrap = document.getElementById('practiceLicense');
                    setTimeout(() => {
                        wrap?.parentElement?.parentElement?.scroll({
                            top: 1000000,
                            behavior: 'smooth',
                        });
                    }, 200);
                }}
                className={s.add}
            >
                + Add another practice license
            </div>
        );
    };

    const renderEditing = () => {
        return (
            <Form
                form={form}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <Form.List name="providerEducationList" initialValue={[{}]}>
                    {
                        (fields, { add, remove }) => {
                            return (
                                <>
                                    {
                                        fields.map(({ key, name, ...restField }, index) => {
                                            const currentWorkingOn = form.getFieldValue(['providerEducationList', name, 'currently']);

                                            return (
                                                <div>
                                                    <div key={key} className={s.wrap}>
                                                        <div
                                                            id={`Experience_${index + 1}`}
                                                        />
                                                        <div className={s.label}>{`Education ${index + 1}`}</div>
                                                        <Divider style={{ margin: '4px 0px 12px' }} />
                                                        <div className={s.additionalForm}>
                                                            <Form.Item
                                                                {...restField}
                                                                label="School"
                                                                name={[name, 'schoolName']}
                                                                rules={[
                                                                    { required: true, message: 'School name is a required field' },
                                                                ]}
                                                            >
                                                                <Input type="text" />
                                                            </Form.Item>
                                                            <div className="flex gap-4">
                                                                <Form.Item
                                                                    {...restField}
                                                                    label="Major"
                                                                    name={[name, 'major']}
                                                                    style={{ marginBottom: '8px', flex: 1 }}
                                                                    validateFirst
                                                                    rules={[
                                                                        { required: true, message: 'Major is a required field' },
                                                                    ]}
                                                                >
                                                                    <Input type="text" placeholder="Ex. Nurse practitioner" />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    {...restField}
                                                                    label="Degree"
                                                                    name={[name, 'degree']}
                                                                    style={{ marginBottom: '8px', flex: 1 }}
                                                                    validateFirst
                                                                    rules={[
                                                                        { required: true, message: 'Degree is a required field' },
                                                                    ]}
                                                                >
                                                                    <Select fieldNames={{ label: 'content', value: 'dictKey' }} options={degreeOptions} />
                                                                </Form.Item>
                                                            </div>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'currently']}
                                                                valuePropName="checked"
                                                            >
                                                                <Checkbox
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            form.setFieldValue(['providerEducationList', name, 'endDate'], undefined);
                                                                        }
                                                                        forceUpdate({});
                                                                    }}
                                                                >
                                                                    I am currently pursuing this degree
                                                                </Checkbox>
                                                            </Form.Item>
                                                            <div className="flex gap-4">
                                                                <Form.Item
                                                                    {...restField}
                                                                    style={{ flex: 1 }}
                                                                    label="Start Date"
                                                                    name={[name, 'startDate']}
                                                                    rules={[
                                                                        {
                                                                            validator: (rules, value) => {
                                                                                const endDateValue = form.getFieldValue('providerEducationList')?.[index]?.endDate;

                                                                                if (endDateValue && endDateValue.isBefore(value)) {
                                                                                    return Promise.reject(
                                                                                        new Error('start date cannot be later than the end date'),
                                                                                    );
                                                                                }
                                                                                return Promise.resolve(true);
                                                                            },
                                                                        },
                                                                    ]}
                                                                >
                                                                    <DatePicker
                                                                        onChange={() => {
                                                                            forceUpdate({});
                                                                            form.validateFields();
                                                                        }}
                                                                        allowClear
                                                                        picker="month"
                                                                        disabledDate={(current) => {
                                                                            const endDateValue = form.getFieldValue('providerEducationList')?.[index]?.endDate;
                                                                            if (!endDateValue || currentWorkingOn) {
                                                                                return false;
                                                                            }
                                                                            return current.valueOf() > endDateValue.valueOf();
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                </Form.Item>

                                                                {!currentWorkingOn &&
                                                                    <Form.Item
                                                                        {...restField}
                                                                        style={{ flex: 1 }}
                                                                        label="End Date"
                                                                        name={[name, 'endDate']}
                                                                        rules={[
                                                                            {
                                                                                validator: (rules, value) => {
                                                                                    const startDateValue = form.getFieldValue('providerEducationList')?.[index]?.startDate;

                                                                                    if (startDateValue && value && value.isBefore(startDateValue)) {
                                                                                        return Promise.reject(
                                                                                            new Error('start date cannot be later than the end date'),
                                                                                        );
                                                                                    }

                                                                                    return Promise.resolve(true);
                                                                                },
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <DatePicker
                                                                            onChange={() => {
                                                                                forceUpdate({});
                                                                                form.validateFields();
                                                                            }}
                                                                            allowClear
                                                                            picker="month"
                                                                            disabledDate={(current) => {
                                                                                const startDateValue = form.getFieldValue('providerEducationList')?.[index]?.startDate;
                                                                                if (!startDateValue) {
                                                                                    return false;
                                                                                }
                                                                                return current.valueOf() < startDateValue.valueOf();
                                                                            }}
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Form.Item>

                                                                }
                                                            </div>
                                                        </div>
                                                        <div className={s.formFooter}>
                                                            <Button onClick={() => remove(index)} danger>Remove</Button>
                                                        </div>
                                                    </div>
                                                    {
                                                        name === fields.length - 1 && renderAdd(add)
                                                    }
                                                </div>
                                            );
                                        })
                                    }
                                    {fields.length === 0 && renderAdd(add)}
                                </>
                            );
                        }
                    }
                </Form.List>
            </Form>

        );
    };

    const renderReview = () => {
        if (!currentValue?.length) {
            return (
                <div>
                    Not provided
                </div>
            );
        }
        const formatDate = (day: string) => dayjs(day).format('YYYY-MM');
        return initValue?.map((item, index) => {
            const {
                schoolName,
                major,
                degree,
                startDate,
                endDate,
                currently,
            } = item;
            return (
                <div key={index} className={s.row}>
                    <div className={s.education}>
                        <div className={s.logoBox}>
                            <div className={s.logo} style={{ backgroundImage: `url(${ExperiencePlaceholder})` }} />
                        </div>
                        <div className={s.intro}>
                            <div className={s.name}>{schoolName}</div>
                            <div className={s.title}>{degree} of {major}</div>
                            {
                                startDate &&
                                <div className={s.timeloc}>
                                    {
                                        currently ?
                                            <>
                                                <span>{formatDate(startDate)}</span>
                                                <span>-</span>
                                                <span>Present</span>
                                            </>
                                            :
                                            <>
                                                <span>{formatDate(startDate)}</span>
                                                <span>-</span>
                                                <span>{formatDate(endDate)}</span>
                                            </>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            );
        });
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default Education;
